// Client-error pipeline. Collects errors from four sources — `window` "error"
// (`onerror`), `unhandledrejection`, the app log facade (`reportClientError`,
// kind="log"), and Pusher connection-health signals (`reportPusherHealth`,
// kind="pusher") — into one bounded, deduped queue that rum.ts flushes to
// /api/client-errors on the same visibility/pagehide triggers as vitals.
//
// Bounds (an error storm must never DoS the ingest or the browser):
//  - dedupe by hash(message + source + line) for the whole session,
//  - at most 20 errors per flushed batch,
//  - at most 100 errors per session.
//
// Every field is truncated to the backend's max_length so one oversize error can
// never 422 the entire batch (the backend rejects the whole body on any overflow).
// The pipeline is DORMANT until initRum enables it (a sampled-out page reports
// nothing) — `reportClientError` is then a no-op, so callers need no guard.

import type { ClientError } from "./types";

const MAX_PER_BATCH = 20;
const MAX_PER_SESSION = 100;
const MESSAGE_MAX = 1024; // ClientError.message
const STACK_MAX = 4096; // ClientError.stack
const SOURCE_MAX = 256; // ClientError.source

let enabled = false;
let queue: ClientError[] = [];
let seen = new Set<string>();
let sessionCount = 0;

/** Flip the pipeline on/off. initRum enables it only when the page is sampled in. */
export function setErrorsEnabled(on: boolean): void {
  enabled = on;
}

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

/** Stable session-dedupe key: hash(message + source + line) (djb2, unsigned). */
function hashKey(message: string, source: string | null | undefined, line: number | null | undefined): string {
  const basis = `${message} ${source ?? ""} ${line ?? ""}`;
  let h = 5381;
  for (let i = 0; i < basis.length; i += 1) {
    h = (Math.imul(h, 33) + basis.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

/** Enqueue one error, applying dedupe + both caps. INTERNAL. */
function enqueue(error: ClientError): void {
  if (!enabled) return;
  const key = hashKey(error.message, error.source, error.line);
  if (seen.has(key)) return; // already reported this exact error this session
  if (sessionCount >= MAX_PER_SESSION) return; // session budget spent
  if (queue.length >= MAX_PER_BATCH) return; // batch full — dropped until next flush
  seen.add(key);
  sessionCount += 1;
  queue.push(error);
}

function safeStringify(data: Record<string, unknown>): string {
  try {
    return JSON.stringify(data);
  } catch {
    return "[unserializable]";
  }
}

/**
 * Report a client-side error / diagnostic entry (kind="log"). This is the diag
 * sink the app log facade forwards `error()` calls to, so client crashes surface
 * in Cloud Logging. Signature matches `log.error`: (scope, message, data?, error?).
 * A no-op until RUM is initialized + sampled in.
 */
export function reportClientError(
  scope: string,
  message: string,
  data?: Record<string, unknown>,
  error?: unknown,
): void {
  if (!enabled) return;
  const hasData = data != null && Object.keys(data).length > 0;
  const composed = hasData ? `[${scope}] ${message} ${safeStringify(data)}` : `[${scope}] ${message}`;
  const stack = error instanceof Error && error.stack ? error.stack : undefined;
  enqueue({
    kind: "log",
    message: truncate(composed, MESSAGE_MAX),
    stack: stack != null ? truncate(stack, STACK_MAX) : null,
    source: null,
    line: null,
    col: null,
  });
}

/**
 * Report a Pusher connection-health signal (kind="pusher"). Wired as the consumer
 * of the pusher manager's inverted health callback (`setPusherHealthHandler`) so
 * the manager never imports rum. `event.kind` is the sub-kind
 * (subscription_error | idle_disconnect | reopen | refcount_underflow).
 */
export function reportPusherHealth(event: { kind: string; detail?: string }): void {
  if (!enabled) return;
  const detail = event.detail != null && event.detail !== "" ? ` ${event.detail}` : "";
  enqueue({
    kind: "pusher",
    message: truncate(`pusher:${event.kind}${detail}`, MESSAGE_MAX),
    stack: null,
    source: null,
    line: null,
    col: null,
  });
}

/**
 * Install the global `error` + `unhandledrejection` listeners. Returns a remover.
 * (admin has no log facade, so these handlers are its only error source.)
 */
export function installGlobalErrorHandlers(): () => void {
  const onError = (event: ErrorEvent): void => {
    enqueue({
      kind: "onerror",
      message: truncate(event.message || "uncaught error", MESSAGE_MAX),
      stack: event.error instanceof Error && event.error.stack ? truncate(event.error.stack, STACK_MAX) : null,
      source: event.filename ? truncate(event.filename, SOURCE_MAX) : null,
      line: event.lineno ?? null,
      col: event.colno ?? null,
    });
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    const reason: unknown = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    enqueue({
      kind: "unhandledrejection",
      message: truncate(message || "unhandled rejection", MESSAGE_MAX),
      stack: reason instanceof Error && reason.stack ? truncate(reason.stack, STACK_MAX) : null,
      source: null,
      line: null,
      col: null,
    });
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return (): void => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}

/** Take (and clear) the current error batch — at most `MAX_PER_BATCH` items. */
export function drainErrors(): ClientError[] {
  if (queue.length === 0) return [];
  const batch = queue;
  queue = [];
  return batch;
}

/** Current queued (un-flushed) error count — test/introspection aid. */
export function errorQueueSize(): number {
  return queue.length;
}

/** Test-only: wipe pipeline state (queue, dedupe set, session budget, enabled). */
export function __resetErrors(): void {
  enabled = false;
  queue = [];
  seen = new Set();
  sessionCount = 0;
}
